import React from 'react';
import classnames from 'classnames';

import { IGridSettings, IViewPort } from '@msdyn365-commerce/core-internal';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';

import { ICusWishlistResources } from '../../cus-wishlist.props.autogenerated';
import { WISHLIST_MODULE_NAME } from '../../cus-wishlist.const';
import { MappedProduct } from '../../../../integrations/relewise/types/mapped';
import { ImageWithLoading } from '../../../../shared/components/image-with-loading/image-with-loading';
import { MemberPriceTag } from '../../../../shared/components/member-price-tag/member-price-tag';
import { ProductLabels } from '../../../../shared/components/product-labels/product-labels';

interface Props {
    product: MappedProduct;
    imageSettings: IViewPort;
    gridSettings: IGridSettings | undefined;
    resources: ICusWishlistResources;
    telemetry: ITelemetry;
    maxProductLabels: number | undefined;
    hideCartButton: boolean;
    isBusy: boolean;
    onRemove: (product: MappedProduct) => Promise<{ success: boolean }>;
    onAddToCart: (product: MappedProduct) => void;
}

export const WishlistItem = ({
    product,
    imageSettings,
    gridSettings,
    resources,
    telemetry,
    maxProductLabels,
    hideCartButton,
    isBusy,
    onRemove,
    onAddToCart
}: Props) => {
    const [isRemoving, setIsRemoving] = React.useState(false);
    const [hasRemoveError, setHasRemoveError] = React.useState(false);

    const handleRemove = async () => {
        setIsRemoving(true);
        setHasRemoveError(false);
        const { success } = await onRemove(product);
        setHasRemoveError(!success);
        setIsRemoving(false);
    };

    return (
        <li className='bs-wishlist-item' data-testid='wishlist-product'>
            <a className='bs-wishlist-item__link' href={product.productPageUrl}>
                <div className='bs-wishlist-item__image-container'>
                    <ImageWithLoading
                        wrapperClassname='bs-wishlist-item__image-wrapper'
                        src={product.imgUrl}
                        alt={product.displayName}
                        title={product.displayName}
                        imageSettings={{ viewports: imageSettings }}
                        gridSettings={gridSettings ?? {}}
                        loadFailureBehavior='empty'
                        showEmptyImageThumbnail
                        location={WISHLIST_MODULE_NAME}
                        telemetry={telemetry}
                        variantConcatenatedId={product.concatenatedId}
                    />
                    <ProductLabels labels={product.labels} maxProductLabels={maxProductLabels} />
                </div>
                <span className='bs-wishlist-item__title' data-testid='wishlist-product-title'>
                    {product.displayName}
                </span>
                {product.color?.name && (
                    <span className='bs-wishlist-item__color'>
                        {resources.colorHeading} {product.color.name}
                    </span>
                )}
                <div className='bs-wishlist-item__price'>
                    {!!product.memberPriceData ? (
                        <>
                            <MemberPriceTag memberPrice={product.memberPriceData} className='bs-wishlist-item__final-price' />
                            <span className='bs-wishlist-item__final-price'>{product.basePriceFormatted}</span>
                        </>
                    ) : (
                        <>
                            <span
                                className={classnames('bs-wishlist-item__final-price', {
                                    'bs-wishlist-item__final-price--discounted': product.hasDiscount
                                })}
                            >
                                {product.finalPriceFormatted}
                            </span>
                            {product.hasDiscount && <span className='bs-wishlist-item__original-price'>{product.basePriceFormatted}</span>}
                        </>
                    )}
                </div>
            </a>
            {!hideCartButton && (
                <button
                    className='bs-wishlist-item__add-to-cart'
                    type='button'
                    aria-label={resources.addToCartText}
                    disabled={isBusy}
                    onClick={() => onAddToCart(product)}
                />
            )}
            <button
                className={classnames('bs-wishlist-item__remove', { 'bs-wishlist-item__remove--loading': isRemoving })}
                data-testid='wishlist-remove-button'
                type='button'
                aria-label={resources.removeFromWishlistAriaLabel}
                aria-busy={isRemoving}
                disabled={isBusy}
                onClick={handleRemove}
            />
            {hasRemoveError && (
                <span className='bs-wishlist-item__error' role='alert'>
                    {resources.removeItemFromWishlistError}
                </span>
            )}
        </li>
    );
};
